import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@/environments/environment';
import {
  ApplySparkAuthCodeDto,
  RequestSparkAuthDto,
  SparkAuthStatusDto,
} from '@/app/common/general-types/spark-ads-v3.dto';

/**
 * Spark Ads authorisation: asking a creator for permission to advertise their post,
 * checking whether it has arrived, and applying a code they supplied another way.
 *
 * These routes live under `/tiktok-spark-ads/*` rather than `/tiktok-one/*` because
 * they persist what they read, which is what makes the library's auth filters and
 * the expiry alert possible.
 */
@Injectable({ providedIn: 'root' })
export class SparkAuthService {
  private readonly http = inject(HttpClient);
  private readonly api = `${environment.shout_api.url}/tiktok-spark-ads`;

  /** Status for one post. */
  getStatus(
    videoId: string,
    context?: { briefId?: string; creatorId?: string },
  ): Observable<SparkAuthStatusDto> {
    const params: Record<string, string> = {};
    if (context?.briefId) {
      params['briefId'] = context.briefId;
    }
    if (context?.creatorId) {
      params['creatorId'] = context.creatorId;
    }
    return this.http.get<SparkAuthStatusDto>(`${this.api}/spark-auth-status/${videoId}`, {
      params,
    });
  }

  /**
   * Status for many posts at once, keyed by video id.
   *
   * Served from the API's local mirror, so this is one request rather than one per card.
   */
  getStatuses(
    videoIds: string[],
    briefId?: string,
  ): Observable<Record<string, SparkAuthStatusDto>> {
    const params: Record<string, string> = { videoIds: videoIds.join(',') };
    if (briefId) {
      params['briefId'] = briefId;
    }
    return this.http.get<Record<string, SparkAuthStatusDto>>(
      `${this.api}/spark-auth-statuses`,
      { params },
    );
  }

  /** Asks the creator for a Spark code. Safe to call again — TikTok treats it as idempotent. */
  requestAuth(dto: RequestSparkAuthDto): Observable<SparkAuthStatusDto> {
    return this.http.post<SparkAuthStatusDto>(`${this.api}/request-spark-auth`, dto);
  }

  /**
   * Applies a code the creator supplied outside the app.
   *
   * Returns the refreshed status when `videoId` is given, or `{ applied: true }` for a
   * post from outside SHOUT that has no video to attach to.
   */
  applyCode(dto: ApplySparkAuthCodeDto): Observable<SparkAuthStatusDto | { applied: true }> {
    return this.http.post<SparkAuthStatusDto | { applied: true }>(
      `${this.api}/apply-spark-auth-code`,
      dto,
    );
  }
}
